/**
 * TA 的朋友（新版）的查询：列表与详情，按账号 + 宠物（+ 朋友）分键（见 ./petFriends 的 petFriendsKey / petFriendKey）。
 *
 * 读口从哪来：先看外面有没有用 PetFriendsReaderProvider 换一个（测试、以后的演示样本用）；没有就按数据模式选——
 * live 用全站唯一的 HTTP 客户端，演示用不编朋友的演示读口。和服务注册表一样，“连不上就用假数据”不由页面决定。
 * shared 的 SocialService 开放后，usePetFriendsReader 改成从 useServices().social 取，这个 Provider 可以删掉。
 */
import { createContext, useContext, type ReactNode } from "react";
import { useQuery } from "@tanstack/react-query";
import { apiClient } from "@/shared/api/client";
import { env } from "@/shared/config/env";
import { useOptionalCurrentHousehold } from "@/shared/session/householdContext";
import { petFriendKey, petFriendsKey } from "./petFriends";
import { demoPetFriendsReader, livePetFriendsReader, type PetFriendsReader } from "./petFriends.service";

const ReaderContext = createContext<PetFriendsReader | null>(null);

export function PetFriendsReaderProvider({ reader, children }: { reader: PetFriendsReader; children: ReactNode }) {
  return <ReaderContext.Provider value={reader}>{children}</ReaderContext.Provider>;
}

let live: PetFriendsReader | null = null;
let demo: PetFriendsReader | null = null;

export function usePetFriendsReader(): PetFriendsReader {
  const injected = useContext(ReaderContext);
  if (injected) return injected;
  if (env.dataMode === "fixture") return (demo ??= demoPetFriendsReader());
  return (live ??= livePetFriendsReader(apiClient));
}

/** 当前账号（没有家庭上下文时为 null：演示、单独挂的页面）。 */
function useUserId(): string | null {
  return useOptionalCurrentHousehold()?.userId ?? null;
}

export function usePetFriendList(petId: string | null) {
  const reader = usePetFriendsReader();
  const userId = useUserId();
  return useQuery({
    queryKey: petFriendsKey(userId, petId),
    queryFn: ({ signal }) => reader.friends(petId!, signal),
    enabled: Boolean(petId),
  });
}

export function usePetFriendDetail(petId: string | null, friendId: string | null) {
  const reader = usePetFriendsReader();
  const userId = useUserId();
  return useQuery({
    queryKey: petFriendKey(userId, petId, friendId),
    queryFn: ({ signal }) => reader.friend(petId!, friendId!, signal),
    enabled: Boolean(petId && friendId),
  });
}
